import React from 'react';
import MediaComponent from '@minecraft.media-component';
import { MediaHelper } from '@blocs.common';
import { Media, MediaTypeCodeEnum, SubmissionMedia } from '../../graphql-operations';
import { BasicContainer, ButtonAsWrapper, DetailTextLarge, FlexCol, GridDynamic } from '../../atoms';
import { EditPrimaryStatus } from './EditPrimaryStatus';
import { DeleteMedia } from './DeleteMedia';
import { SelectMedia } from './SelectMedia';
import { SizeType } from '../../components/CloudinaryPhoto';

type ReadOnlyMediaItemProps = {
  mediaItem: Media | SubmissionMedia;
  onPreviewMediaClick?: (params?: { mediaItem: Media | SubmissionMedia }) => void;
  isPrimary?: boolean;
  disabled?: boolean;
  size?: SizeType;
  'data-qa-id'?: string;
  'data-testid'?: string;
  /** if you already wrapped InteractiveMediaViewer in a CSS grid set this to true, if it's in a FlexCol or FlexRow use false */
  fullWidth?: boolean;
};

type SelectableMediaItemProps = {
  isSelected: boolean;
  onSelectMediaItem: (params: { mediaItem: Media | SubmissionMedia }) => void;
  name: string;
  checkboxLabel: string;
  isSingleSelection?: boolean;
};
type UnselectableMediaItemProps = {
  isSelected?: never;
  onSelectMediaItem?: never;
  name?: never;
  checkboxLabel?: never;
  isSingleSelection?: never;
};
type SelectableProps = SelectableMediaItemProps | UnselectableMediaItemProps;

type EditablePrimaryStatusProps = {
  onChangePrimaryStatus: () => void;
};
type NonEditablePrimaryStatusProps = {
  onChangePrimaryStatus?: never;
};
type EditableProps = EditablePrimaryStatusProps | NonEditablePrimaryStatusProps;

type DeleteableMediaItemProps = {
  onDelete: (params: { mediaItem: Media | SubmissionMedia }) => void;
};
type NonDeleteableMediaItemProps = {
  onDelete?: never;
};
type DeletableProps = DeleteableMediaItemProps | NonDeleteableMediaItemProps;

export type InterativeMediaViewerProps = ReadOnlyMediaItemProps & SelectableProps & EditableProps & DeletableProps;

const AUDIO_CONTAINER_PROPS = ({ isSelected }: { isSelected: boolean }) => ({
  borderSize: 0.125,
  borderPlacement: 'all' as const,
  borderColor: isSelected ? ('primary' as const) : ('default' as const),
  borderRadius: 'sm' as const,
  className: 'cn_overflow-hidden cn_relative',
  padding: '1' as const,
  bgColor: 'card' as const,
});

const DEFAULT_CONTAINER_PROPS = ({ isSelected }: { isSelected: boolean }) => ({
  borderSize: 0.25,
  borderPlacement: 'all' as const,
  borderColor: isSelected ? ('primary' as const) : ('transparent' as const),
  borderRadius: 'xl' as const,
  className: 'cn_overflow-hidden cn_relative',
});

export const InterativeMediaViewer = ({
  size = 'md',
  name,
  disabled,
  isPrimary,
  mediaItem,
  isSelected,
  checkboxLabel,
  isSingleSelection,
  onDelete,
  onSelectMediaItem,
  onPreviewMediaClick,
  onChangePrimaryStatus,
  'data-qa-id': qaId,
  'data-testid': testId,
  fullWidth = false,
}: InterativeMediaViewerProps) => {
  const isAudio = mediaItem.mediaType.code === MediaTypeCodeEnum.Audio;
  const isPhoto = mediaItem.mediaType.code === MediaTypeCodeEnum.Photo;
  const isSelectable =
    typeof isSelected === 'boolean' && typeof onSelectMediaItem === 'function' && typeof name === 'string';
  const isPrimaryStatusEditable = typeof onChangePrimaryStatus === 'function';
  const isDeletable = typeof onDelete === 'function';
  const isDisabled = isPrimary || disabled;
  const mediaName = MediaHelper.getMediaNameWithExtension(mediaItem);

  const ComponentImpl = (
    <FlexCol w="full" alignItems="center" justifyItems="center">
      <FlexCol
        alignItems="normal"
        justifyItems="center"
        w={fullWidth ? 'full' : 'max'}
        {...(isAudio ? AUDIO_CONTAINER_PROPS({ isSelected }) : DEFAULT_CONTAINER_PROPS({ isSelected }))}
        data-testid={testId}
        data-qa-id={qaId}
      >
        {isSelectable && (
          <SelectMedia
            mediaItem={mediaItem}
            isSelected={isSelected}
            onSelectMediaItem={onSelectMediaItem}
            isAudio={isAudio}
            disabled={isDisabled}
            name={name}
            checkboxLabel={checkboxLabel}
            isSingleSelection={isSingleSelection}
          />
        )}
        <ButtonAsWrapper
          aria-label={`View ${mediaName}`}
          onClick={() => onPreviewMediaClick?.({ mediaItem })}
          unsetSizing
        >
          <MediaComponent
            media={mediaItem}
            type={size}
            data-qa-id={`customize-media-${mediaItem.mediaType.code.toLowerCase()}`}
          />
        </ButtonAsWrapper>
        {isPrimaryStatusEditable && (
          <BasicContainer w="full" justifyItems="center">
            <EditPrimaryStatus
              mediaItem={mediaItem}
              isPrimary={isPrimary}
              isSelected={isSelected}
              onChangePrimaryStatus={onChangePrimaryStatus}
            />
          </BasicContainer>
        )}
        {isDeletable && <DeleteMedia mediaItem={mediaItem} onDelete={onDelete} isAudio={isAudio} />}
      </FlexCol>
      {!isPhoto && mediaName && (
        <DetailTextLarge className="cn_max-w-64" data-testid="media-name" truncatableNoWrap align="center">
          {mediaName}
        </DetailTextLarge>
      )}
    </FlexCol>
  );

  if (fullWidth) {
    return ComponentImpl;
  }

  return (
    <GridDynamic columns="repeat(auto-fill, 1fr)" gap="0">
      {ComponentImpl}
    </GridDynamic>
  );
};
